import React, { useEffect, useState, useCallback } from 'react';
import { Dropdown } from 'react-bootstrap';
import { FiBell, FiLogOut, FiUser } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import authService from '../../services/auth.service';
import api from '../../services/api';

interface Notification {
  id: number;
  title: string;
  message: string;
  link?: string;
  is_read: boolean;
  created_at: string;
}

const Topbar: React.FC = () => {
  const navigate = useNavigate();
  const user = authService.getUser();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);

  const loadNotifications = useCallback(async () => {
    try {
      const res = await api.get('/notifications?limit=8');
      const data = res.data?.data || [];
      setNotifications(data);
      setUnreadCount(res.data?.meta?.unread_count ?? data.filter((n: Notification) => !n.is_read).length);
    } catch {
      // no interrumpir UI si falla
    }
  }, []);

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 60000);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  const handleMarkAllRead = async () => {
    try {
      await api.post('/notifications/mark-all-read');
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch { /* silencioso */ }
  };

  const handleMarkRead = async (id: number, link?: string) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* silencioso */ }
    if (link) navigate(link);
  };

  const handleLogout = async () => {
    await authService.logout();
    navigate('/login');
  };

  const getInitials = (name?: string) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'ahora';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
  };

  return (
    <header className="topbar">
      <div className="topbar-left">
        <div className="d-none d-md-block" style={{ width: 220 }} />
      </div>

      <div className="topbar-right">
        {/* Notificaciones */}
        <Dropdown show={showNotifs} onToggle={setShowNotifs} align="end">
          <Dropdown.Toggle
            as="button"
            className="btn btn-light position-relative"
            style={{ background: 'none', border: 'none' }}
          >
            <FiBell size={20} />
            {unreadCount > 0 && (
              <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                style={{ fontSize: '10px' }}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </Dropdown.Toggle>

          <Dropdown.Menu style={{ minWidth: '320px', maxHeight: '420px', overflowY: 'auto' }}>
            <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
              <strong style={{ fontSize: '14px' }}>Notificaciones</strong>
              {unreadCount > 0 && (
                <button className="btn btn-link btn-sm p-0 text-muted" onClick={handleMarkAllRead}
                  style={{ fontSize: '12px' }}>
                  Marcar todas leídas
                </button>
              )}
            </div>

            {notifications.length === 0 ? (
              <div className="text-center text-muted py-4" style={{ fontSize: '13px' }}>
                Sin notificaciones
              </div>
            ) : (
              notifications.map(notif => (
                <Dropdown.Item
                  key={notif.id}
                  onClick={() => handleMarkRead(notif.id, notif.link)}
                  className={`border-bottom py-2 ${!notif.is_read ? 'bg-light' : ''}`}
                  style={{ whiteSpace: 'normal' }}
                >
                  <div className="d-flex gap-2 align-items-start">
                    {!notif.is_read && (
                      <span className="mt-1 flex-shrink-0"
                        style={{ width: 7, height: 7, borderRadius: '50%', background: '#5b69bc', display: 'inline-block' }} />
                    )}
                    <div style={{ flex: 1, paddingLeft: notif.is_read ? 15 : 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: notif.is_read ? 400 : 600 }}>
                        {notif.title}
                      </div>
                      <div style={{ fontSize: '11px', color: '#6c757d', marginTop: 2 }}>
                        {notif.message}
                      </div>
                      <div style={{ fontSize: '10px', color: '#adb5bd', marginTop: 3 }}>
                        {timeAgo(notif.created_at)}
                      </div>
                    </div>
                  </div>
                </Dropdown.Item>
              ))
            )}
          </Dropdown.Menu>
        </Dropdown>

        {/* Usuario */}
        <Dropdown align="end">
          <Dropdown.Toggle
            as="div"
            className="user-dropdown"
            style={{ cursor: 'pointer' }}
          >
            <div className="avatar">
              {getInitials(user?.full_name || user?.email)}
            </div>
            <div className="d-none d-md-block">
              <span className="fw-semibold">{user?.full_name || user?.email}</span>
              <small className="d-block text-muted">
                {user?.roles?.[0]?.replace('_', ' ') || 'Usuario'}
              </small>
            </div>
          </Dropdown.Toggle>

          <Dropdown.Menu>
            <Dropdown.Item onClick={() => navigate('/profile')}>
              <FiUser className="me-2" /> Mi Perfil
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item onClick={handleLogout} className="text-danger">
              <FiLogOut className="me-2" /> Cerrar Sesión
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </header>
  );
};

export default Topbar;
